import React, { useEffect, useState, useReducer, useCallback } from "react";

import {
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    makeStyles,
    InputAdornment,
    TextField,
    CircularProgress,
    Avatar
} from "@material-ui/core";

import {
    DeleteOutline,
    Edit,
    Search,
    CloudUpload
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ProductModal from "../../components/ProductModal";
import ImportProductsModal from "../../components/ImportProductsModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import { getProducts, deleteProduct } from "../../services/products";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles(theme => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    customTableCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    productImage: {
        width: 50,
        height: 50,
        borderRadius: 4,
    },
    colorBox: {
        width: 24,
        height: 24,
        borderRadius: 12,
        margin: 'auto',
        border: '1px solid #ccc'
    }
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_PRODUCTS") {
        const products = action.payload;
        const newProducts = [];

        products.forEach(product => {
            const productIndex = state.findIndex(p => p.id === product.id);
            if (productIndex !== -1) {
                state[productIndex] = product;
            } else {
                newProducts.push(product);
            }
        });

        return [...state, ...newProducts];
    }

    if (action.type === "UPDATE_PRODUCTS") {
        const product = action.payload;
        const productIndex = state.findIndex(p => p.id === product.id);

        if (productIndex !== -1) {
            state[productIndex] = product;
            return [...state];
        } else {
            return [product, ...state];
        }
    }

    if (action.type === "DELETE_PRODUCT") {
        const productId = action.payload;
        const productIndex = state.findIndex(p => p.id === productId);
        if (productIndex !== -1) {
            state.splice(productIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const Products = () => {
    const classes = useStyles();

    const [products, dispatch] = useReducer(reducer, []);
    const [loading, setLoading] = useState(false);
    const [productModalOpen, setProductModalOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [searchParam, setSearchParam] = useState("");
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingProduct, setDeletingProduct] = useState(null);

    useEffect(() => {
        dispatch({ type: "RESET" });
    }, [searchParam]);

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await getProducts();
            dispatch({ type: "LOAD_PRODUCTS", payload: data });
            setLoading(false);
        } catch (err) {
            toastError(err);
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleOpenProductModal = () => {
        setProductModalOpen(true);
        setSelectedProduct(null);
    };

    const handleCloseProductModal = () => {
        setProductModalOpen(false);
        setSelectedProduct(null);
    };

    const handleOpenImportModal = () => {
        setImportModalOpen(true);
    };

    const handleCloseImportModal = () => {
        setImportModalOpen(false);
    };

    const handleEditProduct = (product) => {
        setSelectedProduct(product);
        setProductModalOpen(true);
    };

    const handleDeleteProduct = async (productId) => {
        try {
            await deleteProduct(productId);
            toast.success(i18n.t("products.toasts.deleted"));
        } catch (err) {
            toastError(err);
        }
        setDeletingProduct(null);
        fetchProducts();
    };

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchParam.toLowerCase())
    );

    return (
        <MainContainer>
            <ProductModal
                open={productModalOpen}
                onClose={handleCloseProductModal}
                productId={selectedProduct && selectedProduct.id}
                reload={fetchProducts}
            />
            <ImportProductsModal
                open={importModalOpen}
                onClose={handleCloseImportModal}
                reload={fetchProducts}
            />
            <ConfirmationModal
                title={deletingProduct && `${i18n.t("products.confirmationModal.deleteTitle")} ${deletingProduct.name}?`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteProduct(deletingProduct.id)}
            >
                {i18n.t("products.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <MainHeader>
                <Title>{i18n.t("products.title")}</Title>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder={i18n.t("contacts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={(e) => setSearchParam(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="secondary" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleOpenImportModal}
                        startIcon={<CloudUpload />}
                    >
                        {i18n.t("products.buttons.importCSV")}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenProductModal}
                    >
                        {i18n.t("products.buttons.add")}
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">{i18n.t("products.table.image")}</TableCell>
                                <TableCell align="center">{i18n.t("products.table.name")}</TableCell>
                                <TableCell align="center">{i18n.t("products.table.price")}</TableCell>
                                <TableCell align="center">{i18n.t("products.table.color")}</TableCell>
                                <TableCell align="center">{i18n.t("products.table.actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <>
                                {filteredProducts.map((product) => (
                                    <TableRow key={product.id}>
                                        <TableCell align="center">
                                            {product.image ? (
                                                <Avatar
                                                    src={`${getBackendUrl()}${product.image}`}
                                                    alt={product.name}
                                                    className={classes.productImage}
                                                    variant="rounded"
                                                />
                                            ) : (
                                                <Avatar className={classes.productImage} variant="rounded">
                                                    {product.name.charAt(0)}
                                                </Avatar>
                                            )}
                                        </TableCell>
                                        <TableCell align="center">{product.name}</TableCell>
                                        <TableCell align="center">
                                            {product.price ? `R$ ${parseFloat(product.price).toFixed(2)}` : "-"}
                                        </TableCell>
                                        <TableCell align="center">
                                            {product.color ? (
                                                <div className={classes.colorBox} style={{ backgroundColor: product.color }} />
                                            ) : "-"}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditProduct(product)}
                                            >
                                                <Edit />
                                            </IconButton>

                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setDeletingProduct(product);
                                                    setConfirmModalOpen(true);
                                                }}
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {loading && <TableRow><TableCell colSpan={5} align="center"><CircularProgress /></TableCell></TableRow>}
                            </>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </MainContainer>
    );
};

export default Products;
